// Settings › Integrations (Console D, views/settings.js `#/settings/integrations`; APS-326): what
// this organization has connected, one row per kind, from what already exists: the alert
// destinations (Slack, webhooks, PagerDuty; Settings › Alerts) and the agent handoff (GitHub,
// Linear; Settings › Agents and tokens), each with its state, who connected it and a link to where
// it is configured. Nothing is listed as available before it exists (D103 #11). Both kinds live
// in the alerts service: on a server without it (no ALERTS_KEY, the API answers 503) nothing can
// be connected (503 with not_configured), and the page says so as Alerts does, never as an
// error; a plain 503 is an outage and shows as one (APS-381).
import { useQuery } from '@tanstack/react-query'
import { Link } from '@tanstack/react-router'
import { notConfigured } from '../api/client'
import { alertDestinationsQuery, membersQuery, orgQuery } from '../api/queries'
import type { AlertDestination } from '../api/types'
import { btnClass, PageHead, Section, Status, Table } from '../components/d'
import { Icon, type IconName } from '../components/icons'
import { ErrorState, Loading } from '../components/states'
import { type MessageId, useI18n } from '../i18n'

type Kind = AlertDestination['kind']
const ORDER: Kind[] = ['slack', 'email', 'pagerduty', 'webhook', 'github', 'linear']
const KIND: Record<Kind, { name: string; icon: IconName; what: MessageId; handoff: boolean }> = {
  slack: { name: 'Slack', icon: 'slack', what: 'integrations.what.slack', handoff: false },
  email: { name: 'Email', icon: 'alerts', what: 'integrations.what.email', handoff: false },
  pagerduty: {
    name: 'PagerDuty',
    icon: 'bolt',
    what: 'integrations.what.pagerduty',
    handoff: false,
  },
  webhook: { name: 'Webhooks', icon: 'ext', what: 'integrations.what.webhook', handoff: false },
  github: { name: 'GitHub', icon: 'git', what: 'integrations.what.github', handoff: true },
  linear: { name: 'Linear', icon: 'pr', what: 'integrations.what.linear', handoff: true },
}

export function IntegrationsPage({
  orgId,
  projectId,
  crumb,
  title,
}: {
  orgId: string
  projectId: string
  crumb: string
  title: string
}) {
  const { t, date } = useI18n()
  const org = useQuery(orgQuery(orgId))
  const destinations = useQuery(alertDestinationsQuery(orgId))
  const members = useQuery(membersQuery(orgId))
  const nameOf = (userId: string | null) => {
    const m = members.data?.find((x) => x.user_id === userId)
    return m ? m.name || m.email : null
  }
  const off = destinations.isError && notConfigured(destinations.error)
  const all = destinations.data?.destinations ?? []
  const kinds = ORDER.filter((k) => all.some((d) => d.kind === k))
  /** One row's state: what is wrong first, then what it is doing. */
  const state = (rows: AlertDestination[], handoff: boolean) => {
    if (rows.some((d) => d.revoked_at)) return <Status s="miss" label={t('integrations.revoked')} />
    const paused = rows.find((d) => d.paused_until && new Date(d.paused_until) > new Date())
    if (paused)
      return (
        <Status
          s="miss"
          label={t('integrations.paused', {
            date: date(paused.paused_until ?? '', {
              month: 'short',
              day: 'numeric',
              hour: 'numeric',
              minute: '2-digit',
            }),
          })}
        />
      )
    if (handoff)
      return rows.some((d) => d.handoff_enabled_at) ? (
        <Status s="res" label={t('integrations.handoffOn')} />
      ) : (
        <Status s="mute" label={t('integrations.handoffOff')} />
      )
    return <Status s="res" label={t('integrations.count', { count: rows.length })} />
  }
  const to = (handoff: boolean) => (
    <Link
      className={btnClass('ghost', 'sm')}
      to="/o/$orgId/p/$projectId/settings/$section"
      params={{ orgId, projectId, section: handoff ? 'agents' : 'alerts' }}
    >
      {handoff ? t('integrations.toHandoff') : t('integrations.toAlerts')}
    </Link>
  )
  return (
    <main id="main" className="pg se-pg integrations">
      <PageHead
        crumb={crumb}
        title={title}
        meta={
          org.data && (destinations.data || off)
            ? t('integrations.meta', { org: org.data.name, count: kinds.length })
            : undefined
        }
      />
      <Section
        title={t('integrations.connected')}
        meta={destinations.data ? String(kinds.length) : undefined}
      >
        {destinations.isPending ? (
          <Loading />
        ) : off ? (
          <p className="se-p dim">{t('integrations.off')}</p>
        ) : destinations.isError ? (
          <ErrorState error={destinations.error} retry={() => destinations.refetch()} />
        ) : kinds.length === 0 ? (
          // Said quietly, in one dim line, with where each kind starts (Mosaic, APS-326).
          <p className="se-p dim">
            {t('integrations.none')} {t('integrations.noneBody')}{' '}
            <Link
              className="ul"
              to="/o/$orgId/p/$projectId/settings/$section"
              params={{ orgId, projectId, section: 'alerts' }}
            >
              {t('integrations.toAlerts')}
            </Link>{' '}
            ·{' '}
            <Link
              className="ul"
              to="/o/$orgId/p/$projectId/settings/$section"
              params={{ orgId, projectId, section: 'agents' }}
            >
              {t('integrations.toHandoff')}
            </Link>
          </p>
        ) : (
          <Table label={t('integrations.connectedLabel')}>
            <thead>
              <tr>
                <th className="f">{t('integrations.col.name')}</th>
                <th>{t('integrations.col.what')}</th>
                <th>{t('integrations.col.state')}</th>
                <th>{t('integrations.col.by')}</th>
                <th className="last">
                  <span className="visually-hidden">{t('settings.keys.actions')}</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {kinds.map((k) => {
                const rows = all.filter((d) => d.kind === k)
                const info = KIND[k]
                // Who connected the first of them, and how many others did.
                const first = [...rows].sort((a, b) => a.created_at.localeCompare(b.created_at))[0]
                const by = [...new Set(rows.map((d) => nameOf(d.created_by)).filter(Boolean))]
                return (
                  <tr key={k}>
                    <td className="f">
                      <span className="se-who">
                        <span className="se-ag" aria-hidden="true">
                          <Icon name={info.icon} />
                        </span>
                        <b translate="no">{info.name}</b>
                      </span>
                    </td>
                    <td>
                      {t(info.what)}
                      <span className="t2" translate="no">
                        {[...new Set(rows.map((d) => d.name || d.host))].join(' · ')}
                      </span>
                    </td>
                    <td>{state(rows, info.handoff)}</td>
                    <td>
                      {by.length === 0
                        ? first
                          ? date(first.created_at, { month: 'short', day: 'numeric' })
                          : null
                        : by.length === 1
                          ? by[0]
                          : t('integrations.byMany', { name: by[0] ?? '', count: by.length - 1 })}
                    </td>
                    <td className="last">{to(info.handoff)}</td>
                  </tr>
                )
              })}
            </tbody>
          </Table>
        )}
      </Section>
    </main>
  )
}
